import React from 'react';
import { Box, Button, Card, Container, Grid, Stack, Typography } from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SecurityIcon from '@mui/icons-material/Security';
import ForumIcon from '@mui/icons-material/Forum';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import { useNavigate } from 'react-router-dom';
import PartnerCarousel from './PartnerCarousel';
import { PublicLandingFooter, PublicLandingNav } from './LandingShared';

const primaryColor = '#0A7F74';
const deepColor = '#04534B';

const CommunityLandingPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ bgcolor: '#fff', px: '1px' }}>
      <PublicLandingNav active="home" />

<Container maxWidth="xl" sx={{ pt: { xs: 2, md: 3 }, pb: { xs: 1.5, md: 2 }, px: '16px !important' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            flexDirection: { xs: 'column', md: 'row' },
            borderRadius: 3,
            overflow: 'hidden',
            background: 'linear-gradient(110deg, #065c57 0%, #0a7a72 55%, #076660 100%)',
            minHeight: { xs: 0, md: 170 },
            boxShadow: '0 14px 32px rgba(4, 83, 75, 0.28)'
          }}
        >
          {/* Left: large image ~38% */}
          <Box
            component="img"
            src="/BottomLP.png"
            sx={{
              width: { xs: '100%', md: '38%' },
              height: { xs: 160, md: 'auto' },
              objectFit: 'cover',
              objectPosition: 'center top',
              flexShrink: 0,
              display: 'block'
            }}
          />

          {/* Center: text */}
          <Stack
            sx={{
              flex: 1,
              px: { xs: 2.5, md: 3.5 },
              py: { xs: 2, md: 0 },
              justifyContent: 'center'
            }}
            spacing={0.9}
          >
            <Typography
              sx={{
                color: '#ffffff',
                fontWeight: 800,
                lineHeight: 1.2,
                fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.45rem' }
              }}
            >
              Ready to build a stronger community?
            </Typography>
            <Typography
              sx={{
                color: 'rgba(230, 255, 248, 0.92)',
                fontSize: { xs: '0.86rem', md: '0.95rem' },
                maxWidth: 420,
                lineHeight: 1.55
              }}
            >
              Create an account, invite your members and
              <br />
              start managing your group with confidence.
            </Typography>
          </Stack>

          {/* Right: buttons + big icon */}
          <Stack
            direction="row"
            spacing={1.2}
            sx={{
              alignItems: 'center',
              pr: { xs: 2.5, md: 3 },
              pl: { xs: 2.5, md: 0 },
              pb: { xs: 2.5, md: 0 },
              flexShrink: 0
            }}
          >
            <Button
              variant="contained"
              endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: '1rem !important' }} />}
              onClick={() => navigate('/login?tab=join-community')}
              sx={{
                bgcolor: primaryColor,
                color: '#ffffff',
                textTransform: 'none',
                borderRadius: 2,
                fontWeight: 700,
                px: 2.2,
                py: 1,
                fontSize: '0.88rem',
                whiteSpace: 'nowrap',
                boxShadow: 'none',
                border: '1.5px solid rgba(255,255,255,0.35)',
                '&:hover': { bgcolor: '#0a9185' }
              }}
            >
              Join Now – Get started
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/login?tab=signin')}
              sx={{
                color: '#ffffff',
                borderColor: 'rgba(255,255,255,0.7)',
                textTransform: 'none',
                borderRadius: 2,
                fontWeight: 700,
                px: 2.2,
                py: 1,
                fontSize: '0.88rem',
                whiteSpace: 'nowrap',
                '&:hover': { borderColor: '#ffffff', bgcolor: 'rgba(255,255,255,0.08)' }
              }}
            >
              Sign In
            </Button>
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                justifyContent: 'center',
                width: 64,
                height: 64,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.12)',
                color: 'rgba(255,255,255,0.7)',
                flexShrink: 0
              }}
            >
              <GroupsRoundedIcon sx={{ fontSize: 38 }} />
            </Box>
          </Stack>
        </Box>
      </Container>


      <Container maxWidth="lg" sx={{ pt: { xs: 3, md: 5 }, pb: { xs: 5, md: 7 }, px: '1px !important' }}>
        <Grid container spacing={{ xs: 3, md: 5 }} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                bgcolor: '#E6F4F2',
                px: 1.5,
                py: 0.4,
                borderRadius: 5,
                mb: 2
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 15, color: primaryColor, mr: 0.75 }} />
              <Typography variant="caption" sx={{ color: primaryColor, fontWeight: 700 }}>
                Trusted by communities worldwide
              </Typography>
            </Box>

            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                color: '#0f172a',
                lineHeight: 1.06,
                mb: 1.5,
                fontSize: { xs: '2rem', md: '3.35rem' }
              }}
            >
              Secure. Collaborate.
              <br />
              <Box component="span" sx={{ color: primaryColor }}>
                Grow Together.
              </Box>
            </Typography>

            <Typography sx={{ color: '#6b7280', mb: 2.5, fontSize: { xs: '0.98rem', md: '1.08rem' } }}>
              UTrust is the secure and simple way to record, manage and grow shared assets and
              savings together.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button
                variant="outlined"
                aria-label="Apple download"
                sx={{
                  color: '#111827',
                  borderColor: '#111827',
                  minWidth: 178,
                  minHeight: 52,
                  borderRadius: 2,
                  px: 1.75,
                  '&:hover': { borderColor: '#111827', bgcolor: '#F9FAFB' }
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <AppleIcon />
                  <Box sx={{ textAlign: 'left', lineHeight: 1.1 }}>
                    <Typography sx={{ fontSize: '0.62rem', color: '#374151' }}>Download on the</Typography>
                    <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: '#111827' }}>App Store</Typography>
                  </Box>
                </Stack>
              </Button>
              <Button
                variant="outlined"
                aria-label="Google Play download"
                sx={{
                  color: '#111827',
                  borderColor: '#111827',
                  minWidth: 178,
                  minHeight: 52,
                  borderRadius: 2,
                  px: 1.75,
                  '&:hover': { borderColor: '#111827', bgcolor: '#F9FAFB' }
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <AndroidIcon />
                  <Box sx={{ textAlign: 'left', lineHeight: 1.1 }}>
                    <Typography sx={{ fontSize: '0.62rem', color: '#374151' }}>Get it on</Typography>
                    <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: '#111827' }}>Google Play</Typography>
                  </Box>
                </Stack>
              </Button>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mt: 2 }}>
              {[ 'Collaborative Tools', 'Transparent Records'].map((item) => (
                <Stack key={item} direction="row" spacing={0.5} alignItems="center">
                  <CheckCircleIcon sx={{ color: primaryColor, fontSize: 16 }} />
                  <Typography sx={{ fontSize: '0.8rem', color: '#4b5563', fontWeight: 600 }}>
                    {item}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src="/TopimageLP.png"
              sx={{ width: '100%', borderRadius: 4, border: '1px solid #e5e7eb' }}
            />
          </Grid>
        </Grid>
      </Container>
            <Box sx={{ py: { xs: 4, md: 6 }, borderTop: '1px solid #eef2f1', borderBottom: '1px solid #eef2f1' }}>
        <Container maxWidth="lg" sx={{ px: '1px !important' }}>
          <Box sx={{ mb: 2, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: deepColor, mb: 0.5 }}>
              Our Trusted Partners
            </Typography>
            <Typography sx={{ color: '#6b7280', maxWidth: 680, mx: 'auto', fontSize: '0.95rem' }}>
              Communities and organizations already using UTrust to collaborate and manage records.
            </Typography>
          </Box>
        </Container>
      </Box>
      <PartnerCarousel />
      <Container maxWidth="lg" sx={{ pb: { xs: 4, md: 6 }, px: '1px !important' }}>
        <Grid container spacing={2}>
          {[
            {
              title: 'Secure & Private',
              icon: <SecurityIcon />,
              desc: 'Advanced encryption and bank-level security keep your data safe.'
            },
            {
              title: 'Built for Communities',
              icon: <ForumIcon />,
              desc: 'Collaborate easily with group members and reach goals faster.'
            },
            {
              title: 'Save & Grow Together',
              icon: <TrendingUpIcon />,
              desc: 'Create savings groups, track contributions, and grow together.'
            }
          ].map((feature) => (
            <Grid item xs={12} md={4} key={feature.title}>
              <Card elevation={0} sx={{ p: 2.5, border: '1px solid #e5e7eb', borderRadius: 3, height: '100%' }}>
                <Box sx={{ display: 'inline-flex', p: 1.2, bgcolor: '#E6F4F2', borderRadius: '50%', color: primaryColor, mb: 1.25 }}>
                  {feature.icon}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, fontSize: '1.06rem' }}>
                  {feature.title}
                </Typography>
                <Typography sx={{ color: '#6b7280', fontSize: '0.92rem' }}>{feature.desc}</Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Container maxWidth="lg" sx={{ pb: { xs: 4, md: 6 }, px: '1px !important' }}>
        <Grid container spacing={{ xs: 3, md: 6 }} alignItems="center">
          <Grid item xs={12} md={5}>
            <Typography variant="caption" sx={{ color: primaryColor, fontWeight: 800 }}>
              WHY CHOOSE UTRUST
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, color: deepColor, mt: 1, mb: 2 }}>
              Built for communities.
              <br />
              Designed for trust.
            </Typography>
            <Stack spacing={1.2}>
              {[
                'Accurate records and financial transparency',
                'Community collaboration tools',
                'Real-time alerts and notifications',
                'Accessible on web and mobile'
              ].map((text) => (
                <Stack key={text} direction="row" spacing={1.25} alignItems="center">
                  <CheckCircleIcon sx={{ color: primaryColor, fontSize: 20 }} />
                  <Typography sx={{ color: '#374151' }}>{text}</Typography>
                </Stack>
              ))}
            </Stack>
          </Grid>
          <Grid item xs={12} md={7}>
            <Box component="img" src="/BetweenLP.png" sx={{ width: '100%', borderRadius: 4 }} />
          </Grid>
        </Grid>
      </Container>

      <PublicLandingFooter />
    </Box>
  );
};

export default CommunityLandingPage;
